import React from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import Tooltip from '@atlaskit/tooltip';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import type { AssociatedField, OptionType } from '../../../../../types/layout-items.tsx';
import {
	DefaultValueLabel,
	type DefaultValueControlComponentProps,
} from '../../field-options/option/view.tsx';
import messages from './messages.tsx';

const isOptionsEqual = (maybeOption: unknown, option: OptionType) => {
	if (!maybeOption || typeof maybeOption !== 'object') {
		return false;
	}

	// @ts-expect-error - TS2339 - Property 'id' does not exist on type 'object'.
	return typeof maybeOption.id === 'number'
		? // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'object'.
			option.id === maybeOption.id
		: // @ts-expect-error - TS2339 - Property 'optimisticId' does not exist on type 'object'.
			option.optimisticId === maybeOption.optimisticId;
};

const remove = (defaultValue: unknown, option: OptionType) => {
	if (!Array.isArray(defaultValue)) {
		return defaultValue;
	}

	const result = defaultValue.filter((item) => !isOptionsEqual(item, option));
	return result.length > 0 ? result : undefined;
};

const add = (defaultValue: unknown, option: OptionType) =>
	Array.isArray(defaultValue) ? [...defaultValue, option] : [option];

const onDefaultValueChange =
	(
		field: AssociatedField,
		onFieldUpdated: (newField: AssociatedField) => void,
		option: OptionType,
		isRemoving: boolean,
	) =>
	() => {
		onFieldUpdated({
			...field,
			defaultValue: isRemoving
				? remove(field.defaultValue, option)
				: add(field.defaultValue, option),
		});
	};

export const DefaultValueControlLeft = ({
	field,
	option,
	isSelected,
	onFieldUpdated,
	field: {
		operations: { canModifyDefaultValue },
	},
	tooltipDelay,
}: DefaultValueControlComponentProps) => {
	let tooltipMessage = isSelected ? messages.tooltipUncheck : messages.tooltipCheck;
	if (!canModifyDefaultValue) {
		tooltipMessage = isSelected ? messages.disabledTooltipUncheck : messages.disabledTooltipCheck;
	}
	const intl: IntlShape = useIntl();

	return (
		<Tooltip content={intl.formatMessage(tooltipMessage)} position="bottom" delay={tooltipDelay}>
			<Checkbox
				label=""
				onChange={onDefaultValueChange(field, onFieldUpdated, option, isSelected)}
				name="default"
				value="default"
				isChecked={isSelected}
				isDisabled={!canModifyDefaultValue}
				aria-label={intl.formatMessage(messages.checkboxLabel, {
					option: option.value,
				})}
			/>
		</Tooltip>
	);
};

export const DefaultValueControlRight = ({
	isSelected,
	field: {
		operations: { canModifyDefaultValue },
	},
	tooltipDelay,
	testId,
}: DefaultValueControlComponentProps) => {
	const intl: IntlShape = useIntl();
	if (!isSelected) {
		return null;
	}

	let tooltipMessage = isSelected ? messages.tooltipUncheck : messages.tooltipCheck;
	if (!canModifyDefaultValue) {
		tooltipMessage = isSelected ? messages.disabledTooltipUncheck : messages.disabledTooltipCheck;
	}

	return (
		<Tooltip content={intl.formatMessage(tooltipMessage)} delay={tooltipDelay}>
			<DefaultValueLabel isDisabled={!canModifyDefaultValue} data-testid={testId}>
				{intl.formatMessage(messages.default)}
			</DefaultValueLabel>
		</Tooltip>
	);
};
